import { useEffect, useMemo, useState } from "react";
import { HelpLink } from "../app/HelpLink";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { Button, Checkbox, Field, Input, Select, Why } from "../ui";
import { useCoreDoc } from "../app/useCoreDoc";
import { useLevels } from "../app/levels";
import { FilterField } from "../filters/FilterBuilder";
import { useFilterSources } from "../filters/useFilterSources";
import type { FilterAst } from "../core/filter";
import { useDocSelector } from "../core/useDocSelector";
import { apiCall } from "../core/api";
import { fmtMinutes } from "../core/format";
import { WorklogTabs } from "./DayDone";
import { DEFAULT_SPEC, deletePreset, readPresets, savePreset, type ExportSpec } from "./exportPresets";

interface Preview {
  from: string;
  to: string;
  columns: string[];
  rows: Record<string, unknown>[];
  rowCount: number;
  entries: number;
  rawMinutes: number;
  totalMinutes: number;
  notes: string[];
  filename: string;
  contentType: string;
  content?: string;
}

const PREVIEW_ROWS = 8;

function cell(v: unknown): string {
  if (Array.isArray(v)) return v.join("; ");
  return v === null || v === undefined ? "" : String(v);
}

/**
 * Worklog → Export (I5): tracked time for billing as CSV, JSON or YAML. The server does the work
 * (`POST /api/v1/worklog/export`), so the preview, the download, the API and an automation give the same
 * file. Settings can be saved under a name; saved ones live with the workspace.
 */
export function WorklogExportPage() {
  const { t } = useTranslation();
  const { handle: core, workspaceId } = useCoreDoc();
  const presets = useDocSelector(core, (doc) => readPresets(doc)) ?? [];
  const ro = core?.readOnly ?? true;

  const [spec, setSpec] = useState<ExportSpec>(DEFAULT_SPEC);
  const [presetId, setPresetId] = useState("");
  const [name, setName] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const filterSources = useFilterSources(core, useLevels().settings);

  const set = (patch: Partial<ExportSpec>) => setSpec((s) => ({ ...s, ...patch }));

  const body = useMemo(
    () => ({
      ...spec,
      ...(spec.range === "custom" ? {} : { fromDay: null, toDay: null }),
    }),
    [spec],
  );
  const key = JSON.stringify(body);
  const customIncomplete = spec.range === "custom" && (!spec.fromDay || !spec.toDay);

  useEffect(() => {
    if (!workspaceId || customIncomplete) return;
    const id = setTimeout(() => {
      apiCall<Preview>(workspaceId, "POST", "/worklog/export", {
        spec: JSON.parse(key) as ExportSpec,
        previewRows: PREVIEW_ROWS,
      })
        .then((p) => {
          setPreview(p);
          setError(null);
        })
        .catch((e: Error) => setError(e.message));
    }, 250);
    return () => clearTimeout(id);
  }, [key, workspaceId, customIncomplete]);

  const download = async () => {
    if (!workspaceId) return;
    const full = await apiCall<Preview>(workspaceId, "POST", "/worklog/export", { spec: body });
    const blob = new Blob([full.content ?? ""], { type: full.contentType });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = full.filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const choosePreset = (id: string) => {
    setPresetId(id);
    const p = presets.find((x) => x.id === id);
    if (p) {
      setSpec(p.spec);
      setName(p.name);
    }
  };
  const save = () => {
    if (!core || !name.trim()) return;
    const existing = presets.find((p) => p.name === name.trim());
    setPresetId(savePreset(core.doc, name, spec, existing?.id));
  };

  const opt = (k: string) => t(`worklog.export.${k}`);
  const roundingText =
    spec.roundTo === 0
      ? opt("roundNone")
      : t("worklog.export.roundingIs", {
          to: spec.roundTo,
          mode: opt(`mode.${spec.roundMode}`),
          per: opt(`per.${spec.roundPer}`),
        });

  return (
    <div className="mx-auto w-full max-w-4xl px-4 pb-16 pt-5 mid:px-9 mid:pt-8" data-testid="worklog-export">
      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="text-[26px] font-semibold">{t("nav.worklog")}</h1>
        <HelpLink page="guides/worklog/" />
        <WorklogTabs />
      </div>
      <h2 className="mb-1 text-lg font-semibold">{t("worklog.export.title")}</h2>
      <p className="mb-5 text-sm text-neutral-500">{t("worklog.export.hint")}</p>

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <Field label={opt("saved")} htmlFor="wx-preset">
          <Select
            id="wx-preset"
            value={presetId}
            onChange={(e) => choosePreset(e.target.value)}
            data-testid="wx-preset"
          >
            <option value="">{opt("savedNone")}</option>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={opt("name")} htmlFor="wx-name">
          <Input
            id="wx-name"
            value={name}
            placeholder={opt("namePlaceholder")}
            onChange={(e) => setName(e.target.value)}
            data-testid="wx-name"
          />
        </Field>
        <Button variant="secondary" disabled={ro || !name.trim()} onClick={save} data-testid="wx-save">
          {opt("save")}
        </Button>
        {presetId && (
          <Button
            variant="ghost"
            disabled={ro}
            onClick={() => {
              if (core) deletePreset(core.doc, presetId);
              setPresetId("");
            }}
          >
            {opt("delete")}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        <Field label={opt("range")} htmlFor="wx-range">
          <Select
            id="wx-range"
            value={spec.range}
            onChange={(e) => set({ range: e.target.value as ExportSpec["range"] })}
            data-testid="wx-range"
          >
            {(["thisMonth", "lastMonth", "thisWeek", "lastWeek", "all", "custom"] as const).map((r) => (
              <option key={r} value={r}>
                {opt(`ranges.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
        {spec.range === "custom" ? (
          <div className="flex items-end gap-2">
            <Field label={opt("fromDay")} htmlFor="wx-from">
              <Input
                id="wx-from"
                type="date"
                value={spec.fromDay ?? ""}
                onChange={(e) => set({ fromDay: e.target.value })}
                data-testid="wx-from"
              />
            </Field>
            <Field label={opt("toDay")} htmlFor="wx-to">
              <Input
                id="wx-to"
                type="date"
                value={spec.toDay ?? ""}
                onChange={(e) => set({ toDay: e.target.value })}
                data-testid="wx-to"
              />
            </Field>
          </div>
        ) : (
          <div />
        )}

        {/* which tasks: the filter builder every widget uses (it had three fields of its own, UI cleanup U2) */}
        <div className="col-span-full">
          <Field label={opt("tasks")} htmlFor="wx-filter">
            {filterSources && (
              <FilterField
                id="wx-filter"
                value={(spec.filter ?? undefined) as FilterAst | undefined}
                onChange={(f) => set({ filter: (f ?? null) as ExportSpec["filter"] })}
                sources={filterSources}
                testId="wx-filter"
              />
            )}
          </Field>
        </div>
        <label className="flex items-center gap-3 self-end pb-2 text-sm">
          <Checkbox
            checked={spec.onlyWithNote}
            label={opt("onlyWithNote")}
            onChange={(v) => set({ onlyWithNote: v })}
          />
          <span>{opt("onlyWithNote")}</span>
        </label>

        <Field label={opt("rows")} htmlFor="wx-groupby">
          <Select
            id="wx-groupby"
            value={spec.groupBy}
            onChange={(e) => set({ groupBy: e.target.value as ExportSpec["groupBy"] })}
            data-testid="wx-groupby"
          >
            {(["entry", "task", "day", "taskDay", "group"] as const).map((g) => (
              <option key={g} value={g}>
                {opt(`groupBy.${g}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={opt("minMinutes")} htmlFor="wx-min">
          <Select
            id="wx-min"
            value={spec.minMinutes}
            onChange={(e) => set({ minMinutes: Number(e.target.value) })}
          >
            {[0, 5, 10, 15, 30, 60].map((m) => (
              <option key={m} value={m}>
                {m === 0 ? opt("minNone") : t("worklog.export.minutes", { n: m })}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={opt("rounding")} htmlFor="wx-round">
          <div className="flex flex-wrap gap-2">
            <Select
              id="wx-round"
              value={spec.roundTo}
              onChange={(e) => set({ roundTo: Number(e.target.value) as ExportSpec["roundTo"] })}
              data-testid="wx-round"
            >
              {([0, 1, 6, 10, 15, 30] as const).map((m) => (
                <option key={m} value={m}>
                  {m === 0 ? opt("roundNone") : t("worklog.export.minutes", { n: m })}
                </option>
              ))}
            </Select>
            {spec.roundTo !== 0 && (
              <>
                <Select
                  aria-label={opt("roundMode")}
                  value={spec.roundMode}
                  onChange={(e) => set({ roundMode: e.target.value as ExportSpec["roundMode"] })}
                >
                  {(["up", "nearest", "down"] as const).map((m) => (
                    <option key={m} value={m}>
                      {opt(`mode.${m}`)}
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label={opt("roundPer")}
                  value={spec.roundPer}
                  onChange={(e) => set({ roundPer: e.target.value as ExportSpec["roundPer"] })}
                >
                  {(["entry", "sum"] as const).map((m) => (
                    <option key={m} value={m}>
                      {opt(`per.${m}`)}
                    </option>
                  ))}
                </Select>
              </>
            )}
          </div>
        </Field>
        <Field label={opt("format")} htmlFor="wx-format">
          <div className="flex flex-wrap gap-2">
            <Select
              id="wx-format"
              value={spec.format}
              onChange={(e) => set({ format: e.target.value as ExportSpec["format"] })}
              data-testid="wx-format"
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="yaml">YAML</option>
            </Select>
            {spec.format === "csv" && (
              <>
                <Select
                  aria-label={opt("csvSep")}
                  value={spec.csvSep}
                  onChange={(e) => set({ csvSep: e.target.value as ExportSpec["csvSep"] })}
                >
                  <option value=",">{opt("sep.comma")}</option>
                  <option value=";">{opt("sep.semicolon")}</option>
                  <option value="tab">{opt("sep.tab")}</option>
                </Select>
                <Select
                  aria-label={opt("csvDecimal")}
                  value={spec.csvDecimal}
                  onChange={(e) => set({ csvDecimal: e.target.value as ExportSpec["csvDecimal"] })}
                >
                  <option value=".">{opt("decimal.point")}</option>
                  <option value=",">{opt("decimal.comma")}</option>
                </Select>
              </>
            )}
          </div>
        </Field>
        {spec.format === "csv" && (
          <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
            <label className="flex items-center gap-3 text-sm">
              <Checkbox checked={spec.csvBom} label={opt("csvBom")} onChange={(v) => set({ csvBom: v })} />
              <span>{opt("csvBom")}</span>
            </label>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => set({ csvSep: ";", csvDecimal: ",", csvBom: true })}
              data-testid="wx-german"
            >
              {opt("germanSheets")}
            </Button>
          </div>
        )}
      </div>

      <section className="mt-8" aria-label={opt("preview")}>
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-neutral-500">
            {opt("preview")}
          </h2>
          {preview && (
            <span className="flex items-center gap-1 text-sm" data-testid="wx-total">
              {t("worklog.export.totalLine", {
                total: fmtMinutes(preview.totalMinutes),
                entries: preview.entries,
                from: preview.from,
                to: preview.to,
              })}
              <Why
                title={opt("whyTitle")}
                rows={[
                  [opt("whyEntries"), String(preview.entries)],
                  [opt("whyRaw"), fmtMinutes(preview.rawMinutes)],
                  [
                    opt("minMinutes"),
                    spec.minMinutes ? t("worklog.export.minutes", { n: spec.minMinutes }) : opt("minNone"),
                  ],
                  [opt("rounding"), roundingText],
                  [opt("whyTotal"), fmtMinutes(preview.totalMinutes)],
                ]}
              />
            </span>
          )}
          <Button
            className="ml-auto"
            onClick={() => void download()}
            disabled={!preview || customIncomplete}
            data-testid="wx-download"
          >
            <Download size={14} aria-hidden /> {opt("download")}
          </Button>
        </div>
        {error && <p className="text-sm text-err">{t(`errors.${error}`, { defaultValue: opt("failed") })}</p>}
        {preview && preview.notes.includes("worklogExport.inTwoGroups") && (
          <p className="mb-2 text-[12px] text-neutral-500">{opt("inTwoGroups")}</p>
        )}
        {preview && preview.rows.length === 0 && (
          <div className="rounded-lg border border-dashed border-neutral-300 bg-[var(--surface-2)] px-6 py-8 text-center text-sm text-neutral-500 dark:border-neutral-700">
            {opt("empty")}
          </div>
        )}
        {preview && preview.rows.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
            <table className="w-full text-left text-[12.5px]" data-testid="wx-table">
              <thead className="bg-[var(--surface-2)] text-neutral-500">
                <tr>
                  {preview.columns
                    .filter((c) => c !== "taskId" && c !== "entryId" && c !== "groupId")
                    .map((c) => (
                      <th key={c} className="px-2 py-1.5 font-medium">
                        {c}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={i} className="border-t border-neutral-100 dark:border-neutral-800">
                    {preview.columns
                      .filter((c) => c !== "taskId" && c !== "entryId" && c !== "groupId")
                      .map((c) => (
                        <td key={c} className="max-w-[16rem] truncate px-2 py-1.5">
                          {cell(r[c])}
                        </td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.rowCount > preview.rows.length && (
              <p className="border-t border-neutral-100 px-2 py-1.5 text-[12px] text-neutral-500 dark:border-neutral-800">
                {t("worklog.export.moreRows", { n: preview.rowCount - preview.rows.length })}
              </p>
            )}
          </div>
        )}
        <p className="mt-3 text-[12px] text-neutral-500">{opt("alsoHint")}</p>
      </section>
    </div>
  );
}
